import {
  DEFAULT_ERROR_RETRY_INTERVAL,
  DEFAULT_EVENT_INTERVAL,
  DEFAULT_EVENTS_ROTATION_INTERVAL,
  DEFAULT_LANGUAGE,
  DEFAULT_UPDATE_INTERVAL,
  RoomNameType,
  Theme,
} from '../constants/Config'

export interface ISettings {
  showRoomNameType: RoomNameType
  showEventLogo: boolean
  showDescription: boolean
  clientLogoPath: string
  eventTitle: string
  busyRoomTitle: string
  freeRoomTitle: string
  nextRoomTitle: string
  nextScroll: boolean
  routerScroll: boolean
  routerMin: number
  rotationInterval: number
  theme: Theme
  langCode: string
  updateInterval: number
  errorRetryInterval: number
  eventInterval: number
  deviceId: string
  configFile: string
  configJson: string
}

const parseHashParams = (): ISettings => {
  const { hash, search } = window.location
  let params: { [k: string]: string }
  let settings: ISettings = {
    showRoomNameType: RoomNameType.Json,
    showEventLogo: true,
    showDescription: true,
    clientLogoPath: null,
    eventTitle: null,
    busyRoomTitle: null,
    freeRoomTitle: null,
    nextRoomTitle: null,
    nextScroll: true,
    routerScroll: true,
    routerMin: 1,
    rotationInterval: DEFAULT_EVENTS_ROTATION_INTERVAL,
    theme: Theme.Default,
    langCode: DEFAULT_LANGUAGE,
    updateInterval: DEFAULT_UPDATE_INTERVAL,
    errorRetryInterval: DEFAULT_ERROR_RETRY_INTERVAL,
    eventInterval: DEFAULT_EVENT_INTERVAL,
    configFile: null,
    deviceId: null,
    configJson: null,
  }

  params = hash ? getJsonFromUrl(hash.substr(1)) : getJsonFromUrl(search.substr(1))

  Object.keys(params).forEach((key) => {
    const value = params[key]

    switch (key) {
      case 'roomName':
        if (value === 'none') {
          settings = {
            ...settings,
            showRoomNameType: RoomNameType.None,
          }
        } else if (value === 'json' || value === 'default') {
          settings = {
            ...settings,
            showRoomNameType: RoomNameType.Json,
          }
        } else if (value === 'ics') {
          settings = {
            ...settings,
            showRoomNameType: RoomNameType.Ics,
          }
        } else {
          console.error(`Unknown value '${value}' for parameter '${key}'`) //tslint:disable-line
        }

        break
      case 'eventLogo':
        if (value === 'none') {
          settings = {
            ...settings,
            showEventLogo: false,
          }
        } else if (value === 'event' || value === 'default') {
          settings = {
            ...settings,
            showEventLogo: true,
          }
        } else {
          console.error(`Unknown value '${value}' for parameter '${key}'`) //tslint:disable-line
        }

        break
      case 'clientLogo':
        settings = {
          ...settings,
          clientLogoPath: value === 'none' || value === 'default' ? null : value,
        }
        break
      case 'updateInterval':
        const updateInterval = parseInt(value, 10)
        if (! isFinite(updateInterval) || updateInterval < 1 || updateInterval > 1440) {
          console.error('Update interval must be number from 1 to 1440') //tslint:disable-line
          break
        }

        settings = {
          ...settings,
          updateInterval,
        }

        break
      case 'eventInterval':
        const eventInterval = parseInt(value, 10)

        if (! isFinite(eventInterval) || eventInterval < 1 || eventInterval > 1440) {
          console.error('Event interval must be number from 1 to 1440') //tslint:disable-line
          break
        }

        settings = {
          ...settings,
          eventInterval,
        }

        break
      case 'errorRetryInterval':
        const errorRetryInterval = parseInt(value, 10)

        if (! isFinite(errorRetryInterval) || errorRetryInterval < 3 || errorRetryInterval > 3600) {
          console.error('Error retry interval must be number from 3 to 3600') //tslint:disable-line
          break
        }

        settings = {
          ...settings,
          errorRetryInterval,
        }

        break
      case 'eventTitle':
        settings = {
          ...settings,
          eventTitle: value,
        }

        break
      case 'busyRoomTitle':
        settings = {
          ...settings,
          busyRoomTitle: value,
        }

        break
      case 'freeRoomTitle':
        settings = {
          ...settings,
          freeRoomTitle: value,
        }

        break
      case 'nextRoomTitle':
        settings = {
          ...settings,
          nextRoomTitle: value,
        }

        break
      case 'nextScroll':
        settings = {
          ...settings,
          nextScroll: value === 'true',
        }

        break
      case 'routerScroll':
        settings = {
          ...settings,
          routerScroll: value === 'true',
        }

        break
      case 'routerMin':
        const routerMin = parseInt(value, 10)

        if (routerMin > 10) {
          console.error('Router min value could not be bigger than 10') //tslint:disable-line
          break
        }

        settings = {
          ...settings,
          routerMin,
        }

        break
      case 'rotationInterval':
        const rotationInterval = parseInt(value, 10)

        if (! isFinite(rotationInterval) || rotationInterval < 2 || rotationInterval > 3600) {
          console.error('Rotation interval must be number from 2 to 3600') //tslint:disable-line
          break
        }

        settings = {
          ...settings,
          rotationInterval,
        }

        break
      case 'theme':
        const theme = Object.keys(Theme).filter((k) => Theme[k] === value)[0]
        if (Object.keys(Theme).map((k) => Theme[k]).indexOf(value) > - 1) {
          settings = {
            ...settings,
            theme: Theme[theme],
          }
        } else {
          console.error(`Unknown value '${value}' for parameter '${key}'`) //tslint:disable-line
        }

        break
      case 'langCode':
        settings = {
          ...settings,
          langCode: value,
        }
        break
      case 'eventDescription':
        settings = {
          ...settings,
          showDescription: value === 'true',
        }
        break
      case 'deviceId':
        settings = {
          ...settings,
          deviceId: value,
        }
        break
      case 'configFile':
        settings = {
          ...settings,
          configFile: value === 'none' || value === 'default' ? null : value,
        }
        break
      case 'configJson':
        settings = {
          ...settings,
          configJson: value,
        }
        break
      default:
        console.error(`Unknown parameter '${key}'`) //tslint:disable-line
        break
    }
  })

  return settings
}

const getJsonFromUrl = (query: string): { [k: string]: string } => {
  const result = {}
  query.split('&').forEach((part: string) => {
    if (! part) {
      return
    }

    part = part.split('+').join(' ') // replace every + with space, regexp-free version
    const eq = part.indexOf('=')
    let key = eq > - 1 ? part.substr(0, eq) : part
    const val = eq > - 1 ? decodeURIComponent(part.substr(eq + 1)) : ''
    const from = key.indexOf('[')
    if (from === - 1) {
      result[decodeURIComponent(key)] = val
    } else {
      const to = key.indexOf(']', from)
      const index = decodeURIComponent(key.substring(from + 1, to))
      key = decodeURIComponent(key.substring(0, from))

      if (! result[key]) {
        result[key] = []
      }

      if (! index) {
        result[key].push(val)
      } else {
        result[key][index] = val
      }
    }
  })

  return result
}

export default parseHashParams
